import { Box, Drawer, Stack, TablePagination } from '@mui/material'
import type { PostDevicesSearchRequestBody } from '@ulysses-inc/harami_api_client'
import { type FC, useMemo, useState } from 'react'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import { mergedPlaceNodeIds } from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import DeviceList from './DeviceList'
import DevicesFilterDropDown from './DevicesFilterDropDown'
import { useQueryDevicePlaceNodes } from './api/useQueryDevicePlaceNodes'
import { useQuerySearchDevices } from './api/useQuerySearchDevices'
import EditDevice from './editDevice/EditDevice'

const DEFAULT_ROWS_PER_PAGE = 25

const Devices: FC = () => {
  // 絞り込みはデフォルトで空 (= 全てのキオスク端末を表示)。
  // ユーザー一覧で初期表示時に現場フィルタがかかっていて気づけない問題を作り込まないため、
  // ログインユーザーの所属現場をプリセットしない。
  const [deviceName, setDeviceName] = useState('')
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  // 「自分が所属する現場のキオスク端末のみ表示」トグル。デフォルト OFF = 全件表示。
  // ユーザー一覧で初期表示時に現場フィルタがかかっていて気づけない問題を作り込まないため、
  // ログインユーザーの所属現場はプリセットせず、明示的にトグルした場合のみ絞り込む。
  const [isShowOnlyMyPlaces, setIsShowOnlyMyPlaces] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)

  const { data: devicePlaceNodes } = useQueryDevicePlaceNodes()
  const placeOptions = devicePlaceNodes?.places ?? []
  const placeGroupOptions = devicePlaceNodes?.placeGroups ?? []

  const filter: PostDevicesSearchRequestBody = useMemo(() => {
    const allNodes = devicePlaceNodes?.allNodes ?? []
    // 現場グループは子現場のノードIDに展開してから絞り込む (ユーザー一覧と同じ挙動)
    let expandedPlaceNodeIds = mergedPlaceNodeIds(
      allNodes,
      placeNodeIds,
      placeGroupNodeIds,
      true,
    )
    // 現場/現場グループを明示的に選んでいない場合のみトグルを適用し、
    // 自分が所属する現場に絞り込む (明示選択時はそちらを優先)
    if (
      isShowOnlyMyPlaces &&
      placeNodeIds.length === 0 &&
      placeGroupNodeIds.length === 0
    ) {
      const { loginPlaceIds, loginPlaceGroupIds } = splitLoginPlaceUUIDs()
      expandedPlaceNodeIds = mergedPlaceNodeIds(
        allNodes,
        loginPlaceIds,
        loginPlaceGroupIds,
        true,
      )
    }
    return {
      ...(deviceName ? { deviceName: { $like: deviceName } } : {}),
      ...(expandedPlaceNodeIds.length > 0
        ? { placeNodeId: { $in: expandedPlaceNodeIds } }
        : {}),
    }
  }, [
    deviceName,
    placeNodeIds,
    placeGroupNodeIds,
    isShowOnlyMyPlaces,
    devicePlaceNodes,
  ])

  const { data, isLoading, isError } = useQuerySearchDevices({
    filter,
    offset: page * rowsPerPage,
    limit: rowsPerPage,
  })
  const devices = data?.devices ?? []
  const count = data?.count ?? 0

  const selectedDevice =
    devices.find(device => device.id === selectedDeviceId) ?? null

  const onChangeDeviceName = (value: string) => {
    setDeviceName(value)
    setPage(0)
  }
  const onChangePlaceNodeIds = (value: string[]) => {
    setPlaceNodeIds(value)
    setPage(0)
  }
  const onChangePlaceGroupNodeIds = (value: string[]) => {
    setPlaceGroupNodeIds(value)
    setPage(0)
  }
  const onChangeShowOnlyMyPlaces = (value: boolean) => {
    setIsShowOnlyMyPlaces(value)
    setPage(0)
  }
  const onClearFilter = () => {
    setDeviceName('')
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    setIsShowOnlyMyPlaces(false)
    setPage(0)
  }

  return (
    <Stack>
      <Box sx={{ height: 72, width: '100%', boxSizing: 'border-box' }}>
        <Header>
          <HeaderTitle title="キオスク端末" />
        </Header>
      </Box>

      <Stack sx={{ width: '100%' }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 2 }}
        >
          <DevicesFilterDropDown
            deviceName={deviceName}
            placeNodeIds={placeNodeIds}
            placeGroupNodeIds={placeGroupNodeIds}
            isShowOnlyMyPlaces={isShowOnlyMyPlaces}
            placeOptions={placeOptions}
            placeGroupOptions={placeGroupOptions}
            onChangeDeviceName={onChangeDeviceName}
            onChangePlaceNodeIds={onChangePlaceNodeIds}
            onChangePlaceGroupNodeIds={onChangePlaceGroupNodeIds}
            onChangeShowOnlyMyPlaces={onChangeShowOnlyMyPlaces}
            onClear={onClearFilter}
          />
        </Stack>

        {isError ? (
          <ErrorMessage>キオスク端末一覧の取得に失敗しました</ErrorMessage>
        ) : isLoading ? (
          <Loading />
        ) : (
          <DeviceList
            devices={devices}
            onClickDevice={device => setSelectedDeviceId(device.id)}
          />
        )}
      </Stack>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
        }
        sx={{
          '& .MuiTablePagination-selectLabel': { margin: 0 },
          '& .MuiTablePagination-displayedRows': { margin: 0 },
        }}
      />

      <Drawer
        anchor="right"
        open={selectedDevice !== null}
        onClose={() => setSelectedDeviceId(null)}
        hideBackdrop={false}
        ModalProps={{ keepMounted: false }}
        slotProps={{
          paper: {
            sx: { maxHeight: '100%', width: '100%', maxWidth: 600 },
          },
        }}
      >
        {selectedDevice && (
          <EditDevice
            device={selectedDevice}
            onClose={() => setSelectedDeviceId(null)}
          />
        )}
      </Drawer>
    </Stack>
  )
}

export default Devices
